import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Users, Search, Filter, Shield, UserX, Eye, Loader2, ChevronDown
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const isSuperAdmin = adminUser.admin_role === 'super_admin';

  useEffect(() => {
    fetchUsers();
  }, [roleFilter]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${API}/admin/users?role_filter=${roleFilter}&limit=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(response.data.users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can change roles');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API}/admin/users/${userId}/role`,
        { admin_role: newRole, permissions: [] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User role updated');
      fetchUsers();
    } catch (error) {
      console.error('Failed to update role:', error);
      toast.error('Failed to update role');
    }
  };

  const handleSuspend = async (userId, currentlySuspended) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API}/admin/users/${userId}/suspend?suspended=${!currentlySuspended}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(currentlySuspended ? 'User reactivated' : 'User suspended');
      fetchUsers();
    } catch (error) {
      console.error('Failed to suspend/unsuspend:', error);
      toast.error('Failed to update user status');
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedUser(response.data);
      setShowUserModal(true);
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      toast.error('Failed to load user details');
    }
  };

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleBadge = (role) => {
    if (!role) return <span className="text-xs text-gray-500">Regular User</span>;
    
    const badges = {
      super_admin: 'bg-purple-100 text-purple-800 border-purple-300',
      programme_manager: 'bg-blue-100 text-blue-800 border-blue-300',
      moderator: 'bg-green-100 text-green-800 border-green-300',
      content_editor: 'bg-amber-100 text-amber-800 border-amber-300'
    };
    
    const labels = {
      super_admin: 'Super Admin',
      programme_manager: 'Programme Manager',
      moderator: 'Moderator',
      content_editor: 'Content Editor'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${badges[role]}`}>
        {labels[role]}
      </span>
    );
  };

  const stats = {
    total: users.length,
    admins: users.filter(u => u.admin_role).length,
    registered: users.filter(u => !u.is_anonymous).length,
    anonymous: users.filter(u => u.is_anonymous).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-12 h-12 text-ssdp-teal animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          User Management
        </h1>
        <p className="text-gray-600">
          Manage user accounts, roles, and permissions
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-ssdp-teal" />
            <span className="text-sm font-medium text-gray-600">Total Users</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-medium text-gray-600">Admin Users</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.admins}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-gray-600">Registered</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.registered}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-600">Anonymous</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.anonymous}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, or ID..."
              className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none"
            />
          </div>

          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none"
          >
            <option value="all">All Users</option>
            <option value="admins">Admin Users Only</option>
            <option value="registered">Registered Users</option>
            <option value="anonymous">Anonymous Users</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                  User
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                  Role
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                  Progress
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <motion.tr
                  key={user.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {user.username}
                      </p>
                      <p className="text-sm text-gray-500">
                        {user.email || user.id.slice(0, 20)}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {isSuperAdmin && !user.is_anonymous ? (
                      <select
                        value={user.admin_role || ''}
                        onChange={(e) => handleRoleChange(user.id, e.target.value || null)}
                        className="px-3 py-1 border-2 border-gray-200 rounded-lg text-sm focus:border-ssdp-teal focus:outline-none"
                      >
                        <option value="">Regular User</option>
                        <option value="super_admin">Super Admin</option>
                        <option value="programme_manager">Programme Manager</option>
                        <option value="moderator">Moderator</option>
                        <option value="content_editor">Content Editor</option>
                      </select>
                    ) : (
                      getRoleBadge(user.admin_role)
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <p className="text-gray-900 font-medium">
                        Level {user.level} • {user.xp} XP
                      </p>
                      <p className="text-gray-500">
                        {user.completed_scenarios?.length || 0} scenarios completed
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {user.suspended ? (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-300">
                        Suspended
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => viewUserDetails(user.id)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4 text-gray-600" />
                      </button>
                      {(isSuperAdmin || adminUser.admin_role === 'programme_manager' || adminUser.admin_role === 'moderator') && (
                        <button
                          onClick={() => handleSuspend(user.id, user.suspended)}
                          className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                            user.suspended
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-red-100 text-red-700 hover:bg-red-200'
                          }`}
                        >
                          {user.suspended ? 'Reactivate' : 'Suspend'}
                        </button>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No users found</p>
          </div>
        )}
      </div>

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedUser.user.username}
                </h2>
                <p className="text-gray-600">{selectedUser.user.email || selectedUser.user.id}</p>
              </div>
              <button
                onClick={() => setShowUserModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <span className="text-2xl">×</span>
              </button>
            </div>

            <div className="space-y-6">
              {/* User Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-bold text-gray-900 mb-3">Account Info</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Level:</span>
                    <span className="ml-2 font-semibold">{selectedUser.user.level}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">XP:</span>
                    <span className="ml-2 font-semibold">{selectedUser.user.xp}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Streak:</span>
                    <span className="ml-2 font-semibold">{selectedUser.user.current_streak} days</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Scenarios:</span>
                    <span className="ml-2 font-semibold">{selectedUser.user.completed_scenarios?.length || 0}</span>
                  </div>
                </div>
              </div>

              {/* Progress */}
              <div>
                <h3 className="font-bold text-gray-900 mb-3">Recent Progress</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {selectedUser.progress?.slice(0, 5).map((p, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-3 text-sm">
                      <p className="font-medium text-gray-900">{p.scenario_id}</p>
                      <p className="text-gray-600">
                        {p.completed ? '✓ Completed' : 'In Progress'} • {p.xp_earned} XP
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
